import { LanguageWebsiteService } from './../../../services/language-website-service';
import { CurrencyService } from '../../../services/currency-service';
import { SessionService } from '../../../services/session-service';
import { bindable, ICustomElementViewModel, inject, observable } from 'aurelia';
import './db-currency-language-selector.scss';
import { websiteShortCode } from '../../../environment';
import { CdsAutocomplete } from '@chicksgroup/web-components';
import { Currency } from '../../../services/models/currency/currency';
import { LanguageAbbreviations } from '../../../resources/constants';

const CURRENCY_KEY = 'currency';

@inject(SessionService, CurrencyService, LanguageWebsiteService)
export class DbCurrencyLanguageSelector implements ICustomElementViewModel {
    @bindable selectedCurrency;
    @bindable selectedLanguage;
    languageAutocompleteRef: CdsAutocomplete;
    currencyAutocompleteRef: CdsAutocomplete;
    @observable currencies: { label: string, value: string, items: { label: string, value: string, selected: boolean, icon: string }[] }[];
    activeCurrencies: Currency[];
    @observable languages: { label: string, value: string, items: { label: string, value: string, selected: boolean }[] }[];
    popularLanguages: string[] = ['en', 'es', 'de'];

    showDivider: boolean = true;

    typeaheadController = {
        active: true, onKeydown: () => { }
    };

    constructor(private sessionService: SessionService, private currencyService: CurrencyService, private LanguageWebsiteService: LanguageWebsiteService) {
        this.currencies = [];
    }

    attaching() {
        this.setLanguageLoad();
        this.setCurrencyLoad();
    }

    async setLanguageLoad() {
        const languagesByWebsite = await this.LanguageWebsiteService.getByWebsite();
        const uniqueLangsMap = new Map();
        for (const lang of languagesByWebsite) {
            if (!uniqueLangsMap.has(lang.language.hrefLang)) {
                uniqueLangsMap.set(lang.language.hrefLang, lang);
            }
        }
        const uniqueLanguages = Array.from(uniqueLangsMap.values());

        let storedLanguage = null;
        try {
            storedLanguage = JSON.parse(window.localStorage.getItem('language') || '{}');
        } catch {}
        let selectedLangObj = null;

        if (storedLanguage && storedLanguage.language && storedLanguage.language.hrefLang) {
            selectedLangObj = uniqueLanguages.find(
                lang => lang.language.hrefLang === storedLanguage.language.hrefLang
            );
        }
        if (!selectedLangObj) {
            selectedLangObj = uniqueLanguages[0];
        }

        const trendingLanguages = uniqueLanguages.filter(lang =>
            this.popularLanguages.includes(lang.language.hrefLang)
        );
        const allLanguages = uniqueLanguages.filter(
            lang => !this.popularLanguages.includes(lang.language.hrefLang)
        );

        const trendingItems = trendingLanguages.map(lang => ({
            label: LanguageAbbreviations.find(abbrev => abbrev.abbreviation === lang.language.name)?.name || lang.language.name,
            value: lang.language.hrefLang,
            selected: selectedLangObj && lang.language.hrefLang === selectedLangObj.language.hrefLang
        }));

        const allItems = allLanguages.map(lang => ({
            label: LanguageAbbreviations.find(abbrev => abbrev.abbreviation === lang.language.name)?.name || lang.language.name,
            value: lang.language.hrefLang,
            selected: selectedLangObj && lang.language.hrefLang === selectedLangObj.language.hrefLang
        }));

        this.languages = [
            { label: 'Trending', value: 'trending', items: trendingItems },
            { label: 'All', value: 'all', items: allItems }
        ];

        this.selectedLanguage = selectedLangObj;

        if (this.selectedLanguage) {
            const langName = LanguageAbbreviations.find(abbrev => abbrev.abbreviation === this.selectedLanguage.language.hrefLang)?.name || this.selectedLanguage.language.name;

            this.languageAutocompleteRef.setValue(langName, false);
        }

        this.handleLanguageForHtml();
    }

    async setLanguage(e: CustomEvent) {
        const languageByWebsite = await this.LanguageWebsiteService.getByWebsite();
        const selected = languageByWebsite.find(lang => lang.language.hrefLang === e.detail.value);
        if (selected) {
            this.selectedLanguage = {
                language: {
                    name: selected.language.name,
                    hrefLang: selected.language.hrefLang
                }
            };
            window.localStorage.setItem('language', JSON.stringify(this.selectedLanguage));
        }
        this.handleLanguageForHtml();
    }

    handleLanguageForHtml() {
        if (this.selectedLanguage) {
            const htmlElement = document.querySelector('html');
            if (htmlElement && this.selectedLanguage.language) htmlElement.setAttribute('lang', this.selectedLanguage.language.hrefLang);
        }
    };

    async setCurrencyLoad() {
        const getCurrencyByCountry = await this.sessionService.getCurrencyByCountry();
        this.activeCurrencies = await this.currencyService.getActiveCurrenciesByWebsite(websiteShortCode());
        const filterCurrency = await this.activeCurrencies.find((curr) => curr.code === getCurrencyByCountry);
        this.selectedCurrency = filterCurrency;
        await this.createListCurrency();
    }

    setCurrency(e: CustomEvent) {
        const selectedCurrency = this.activeCurrencies.find(curr => curr.id === Number(e.detail.value));
        if (selectedCurrency) {
            this.selectedCurrency = selectedCurrency;
            window.localStorage[CURRENCY_KEY] = selectedCurrency.code;
            this.currencyAutocompleteRef.setValue(`${selectedCurrency.code} - ${selectedCurrency.description}`, false);
            this.currencyAutocompleteRef.leadingIcon = selectedCurrency.code;
        }
    }

    async createListCurrency() {
        this.currencies = [];
        let storedCurrencyCode = window.localStorage[CURRENCY_KEY];

        if (!storedCurrencyCode) {
            const usdCurrency = this.activeCurrencies?.find(curr => curr.code === 'USD');
            if (usdCurrency) {
                storedCurrencyCode = 'USD';
                window.localStorage[CURRENCY_KEY] = 'USD';
                this.selectedCurrency = usdCurrency;
            }
        }

        const currencyObjectTrending = this.activeCurrencies.filter((curr) => curr.isTrending);
        const currencyTrendingItems = currencyObjectTrending.map((curr) => {
            return {
                label: `${curr.code} - ${curr.description}`,
                value: String(curr.id),
                selected: storedCurrencyCode === curr.code,
                icon: curr.code
            };
        });

        const currencyObjectAll = this.activeCurrencies.filter((curr) => !curr.isTrending);
        const currencyAllItems = currencyObjectAll.map((curr) => {
            return {
                label: `${curr.code} - ${curr.description}`,
                value: String(curr.id),
                selected: storedCurrencyCode === curr.code,
                icon: curr.code
            };
        });

        if (currencyTrendingItems.length <= 0) {
            this.currencies.push({ label: 'All', value: 'all', items: currencyAllItems });
        } else {
            this.currencies.push(
                { label: 'Trending', value: 'trending', items: currencyTrendingItems },
                { label: 'All', value: 'all', items: currencyAllItems }
            );
        }

        if (storedCurrencyCode) {
            const currency = this.activeCurrencies.find((curr) => curr.code === storedCurrencyCode);
            if (currency) {
                this.selectedCurrency = currency;
                this.currencyAutocompleteRef.setValue(`${currency.code} - ${currency.description}`);
                this.currencyAutocompleteRef.leadingIcon = currency.code;
            }
        }

        return this.currencies;
    };
}
